import {Button, Modal} from 'antd'
import {
  docs_deleteCodiDocumentRow_Api,
  docs_getCodiDocumentsRowDetails_Api,
  docs_getCodiExtraDataById_Api,
  docs_getSubstantiveById_Api,
} from 'apis'
import {documentModel} from 'apis/model'
import {codiDocumentSchema, documentSchema} from 'apis/schema'
import classNames from 'classnames'
import EmptyHolder from 'components/EmptyHolder'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import Async from 'modules/asyncCache/components/Async'
import AsyncAction from 'modules/asyncCache/components/AsyncAction'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import PermissionProvider from 'modules/permissions/PermissionProvider'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState,} from 'react'
import {useLocation} from 'react-router-dom'
import {useSelectEntities} from 'redux/entities/useSelectEntities'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {getResponseItem, NullAsDefault, renderIf} from 'views/Shared'
import {CDActionTypes} from '.'
import {UpdateCodiRowType} from './components/Codi/CodiDocumentActions'
import CodiDocumentComments from './components/Codi/CodiDocumentComments'
import CodiDocumentTimeLine from './components/Codi/CodiDocumentTimeLine'
import {AddCodiContent, AmendCodiContent} from './EditForms/AmendCodiContent'
import ArrangeCodiContent from './EditForms/ArrangeCodiContent'
import CancelCodiContent from './EditForms/CancelCodiContent'
import {EditCodiContent, InsertCodiContent} from './EditForms/EditCodiContent'
import LoadingBlock from "../Loading/LoadingBlock";
import {decryptContent} from "../../helpers/dataHelpers";

export const CodiDocumentContentTypes = Object.freeze({
  ROOT: 'root',
  PART: 'part',
  APPENDIX: 'appendix',
  CHAPTER: 'chapter',
  SECTION: 'section',
  ARTICLE: 'article',
  CLAUSE: 'clause',
  POINT: 'point',
  CONTENT: 'content',
})

export const CodiAdjModes = Object.freeze({
  USE_NOTE: { label: 'note', value: 'use-note' },
  USE_REFERENCES: { label: 'references', value: 'use-references' },
})

const CodiDocumentContext = React.createContext({})

const StickyBottomBar = ({ style, children, className }) => (
  <div
    style={style}
    className={classNames(
      'z-10 p-2 sticky bottom-0 background pointer-events-none',
      className
    )}>
    <div className="flex items-center gap-2 pointer-events-auto justify-end p-2">
      {children}
    </div>
  </div>
)

export const UseConfirmDeleteAction = ({
  query,
  values,
  apiInfo,
  children,
  onSuccess,
  title = 'delete',
  message = 'are you sure',
  ...props
}) => {
  const t = useTranslate()
  if (_.isFunction(children)) {
    return (
      <AsyncAction
        {...{
          query,
          values,
          apiInfo,
          onSuccess,
          onError: notifyOnError(t),
          ...props,
        }}>
        {({ handleAsyncAction = NullAsDefault, ...rest }) =>
          children({
            ...rest,
            confirm: (values, query) => {
              showDeleteConfirmDialog({
                title,
                message,
                translate: t,
                onOk: () => {
                  handleAsyncAction(values, query)
                },
              })
            },
          })
        }
      </AsyncAction>
    )
  }
  return children
}

export function getDecryptedCodiRows(codi_document) {
  if (_.isEmpty(codi_document ?? undefined)) {
    return codi_document
  }
  return {
    ...(codi_document ?? {}),
    rows: codi_document?.rows
      ? decryptContent(codi_document?.rows, codi_document)
      : [],
  }
}

export const UseCodiDocumentById = ({ id, children, autoLoading = true }) => {
  const cache = useSelectEntities(id, codiDocumentSchema) ?? {}
  const render = useCallback(
    (item, params) =>
      children({
        id,
        ...(params ?? {}),
        item: getDecryptedCodiRows(item),
      }),
    [id]
  )
  if (_.isFunction(children)) {
    if (_.isEmpty(cache)) {
      return (
        <Async
          key={id}
          {...{
            query: bindQueryParam({ id }),
            apiInfo: docs_getSubstantiveById_Api,
          }}>
          {({ response, isLoading = false, ...rest }) => {
            if (autoLoading && !!isLoading) {
              return <LoadingBlock />
            }
            const item = getResponseItem(response)
            return render(item, { isLoading, ...rest })
          }}
        </Async>
      )
    } else {
      return render(cache)
    }
  }
  return children
}

export const CodiDocumentProvider = ({
  id,
  children,
  useExtra = true,
  storage = {
    row_detail: {},
    root_selected: true,
  },
  ...props
}) => {
  const t = useTranslate()

  const history = useHistory()

  const location = useLocation()

  const [activeKey, setActiveKey] = useState()

  const { setOpenMenu = NullAsDefault } = useContext(LayoutContext)

  const [modal_action, set_modal_action] = useState({
    type: undefined,
    value: undefined,
  })

  const {
    response,
    isLoading = false,
    ...rest
  } = useAsync({
    apiInfo: docs_getSubstantiveById_Api,
    query: bindQueryParam({ id }),
  })

  const [data_storage, set_data_storage] = useState(storage)

  useEffect(() => {
    if (location.state?.reload) {
      const { handleAsyncAction } = rest
      handleAsyncAction()
    }
  }, [location.state])

  const raw_item = getResponseItem(response)

  const item = useSelectEntities(id, codiDocumentSchema, raw_item)


  const basic_document = _.get(item, 'document')

  const document = useSelectEntities(
    documentModel.getId(basic_document),
    documentSchema,
    basic_document
  )

  const error = getResponseItem(response, 'error')

  const errorMessage = _.get(response, 'errorMessages.error')

  const error_code = _.get(response, 'errorMessages.error_code')

  const status = {
    title: getResponseItem(response, 'status'),
    description: errorMessage,
  }

  const isSucceed = Number(_.get(response, 'status')) === 200

  const { handleAsyncAction: getDetail = NullAsDefault, ...history_info } =
    useAsyncAction({
      apiInfo: docs_getCodiDocumentsRowDetails_Api,
      onError: () => {
        set_data_storage({
          ...data_storage,
          row_detail: {},
        })
      },
      onSuccess: (__, data) => {
        const item = _.get(data, 'response.data')
        set_data_storage({
          ...data_storage,
          row_detail: item,
        })
      },
    })

  const getRowCacheId = (row_id) =>
    docs_getCodiDocumentsRowDetails_Api.path.replace(':id', row_id)

  const reset = (reload = true) => {
    set_data_storage(storage)
    if (reload) {
      history.push({
        ...location,
        state: {
          ...location.state,
          reload: Date.now(),
        },
      })
    }
  }

  const { handleAsyncAction: deleteCodiContent } = useAsyncAction({
    apiInfo: docs_deleteCodiDocumentRow_Api,
    onError: notifyOnError(t),
    onSuccess: () => {
      successNotify(NotificationActionTypes.DELETE, t)
      onDismiss()
    },
  })

  const handleAction = useCallback(
    (action, data = {}) => {
      let _storage = {}

      const id = data?.id

      const { index_typecd } = data ?? {}

      const { selected_item } = data_storage ?? {}

      const _current_id = selected_item?.id

      const _getData = (selected_item, useDetail = true) => {
        let _storage = { selected_item }
        if (id && id !== _current_id) {
          if (![CodiDocumentContentTypes.ROOT].includes(index_typecd)) {
            if (useDetail) {
              getDetail(undefined, { id })
            }
          } else {
            _storage.row_detail = {}
          }
        }
        return _storage
      }

      switch (action) {
        case CDActionTypes.ROW_CLICKED:
          _storage = _getData(data, data?.useDetail)
          break
        case CDActionTypes.SHOW_ADMIN_MODAL:
        case CDActionTypes.SHOW_INFO_MODAL:
        case CDActionTypes.SHOW_KEYWORD_MODAL:
        case CDActionTypes.SHOW_INDEXES_MODAL:
        case CDActionTypes.SHOW_LINKED_ARTICLES_MODAL:
        case CDActionTypes.SHOW_LINKED_DOCUMENTS_MODAL:
        case CDActionTypes.SHOW_LINKED_CODI_DOCUMENTS_MODAL:
        case CDActionTypes.SHOW_LINKED_PRECEDENT_DOCUMENTS_MODAL:
          set_modal_action({
            type: action,
            value: data,
          })
          break
        case CDActionTypes.SHOW_DETAIL_MODAL:
        case CDActionTypes.SHOW_COMMENT_MODAL:
          _storage = _getData(data)
          set_modal_action({
            type: action,
            value: data,
          })
          break
        case CDActionTypes.ADD_CONTENT:
        case CDActionTypes.AMEND_CONTENT:
        case CDActionTypes.CANCEL_CONTENT:
        case CDActionTypes.INSERT_CONTENT:
        case CDActionTypes.EDIT_CONTENT:
        case CDActionTypes.ARRANGE_CONTENT:
        case CDActionTypes.UPDATE_CODI_ROW_TYPE:
          set_modal_action({
            type: action,
            value: data?.selected_item ?? selected_item,
          })
          break
        case CDActionTypes.DELETE_CONTENT:
          showDeleteConfirmDialog({
            translate: t,
            title: 'Delete this content',
            message: 'You want to delete this content',
            onOk: () => {
              const row_id = selected_item?.id
              if (row_id) {
                deleteCodiContent({}, { id, row_id })
              }
            },
          })
          break
        default:
          break
      }

      set_data_storage({
        ...data_storage,
        ..._storage,
        root_selected: index_typecd === CodiDocumentContentTypes.ROOT,
      })
    },
    [id, item, data_storage]
  )

  const onDismiss = () => {
    set_modal_action({})
  }

  const handleMenuClick = (key) => {
    let timeout
    const _active_keys = _.isArray(key) ? Array.from(key).join(',') : key
    setActiveKey(_active_keys)
    timeout = setTimeout(() => {
      setOpenMenu(true)
      clearTimeout(timeout)
    }, 0)
  }

  const renderModalFooter = useCallback(
    (actions = null, { isSubmitting } = {}) => (
      <StickyBottomBar>
        <Button
          // ghost={true}
          // type="primary"
          onClick={onDismiss}
          disabled={!!isSubmitting}
          className="button-rounded-full no-border font-medium">
          {t('cancel')}
        </Button>
        {actions}
      </StickyBottomBar>
    ),
    [t]
  )

  const modalDismissRef = useRef(NullAsDefault)

  const renderModal = ({
    title,
    onClose = NullAsDefault,
    withProps = () => ({}),
    Component = EmptyHolder,
  }) => {
    return (
      <FullSideModalLayout
        center={true}
        onCancel={() => {
          onClose()
          onDismiss()
        }}
        headerStyle={{ padding: '0.5rem' }}
        title={_.isString(title) ? t(title) : title}>
        <Component {...(withProps() ?? {})} />
      </FullSideModalLayout>
    )
  }

  const modal_content = useMemo(() => {
    const codi_header = (
      <span className="max-lines-2 font-medium text-sm text-color-000">
        {getTitle(item)}
      </span>
    )
    const params = modal_action?.value ?? {}
    switch (modal_action?.type) {
      case CDActionTypes.SHOW_DETAIL_MODAL:
        return renderModal({
          title: codi_header,
          onClose: modalDismissRef?.current,
          withProps: () => ({ className: 'p-2' }),
          Component: CodiDocumentTimeLine,
        })
      case CDActionTypes.SHOW_COMMENT_MODAL:
        return renderModal({
          title: codi_header,
          onClose: modalDismissRef?.current,
          withProps: () => ({ className: 'p-2' }),
          Component: CodiDocumentComments,
        })
      case CDActionTypes.SHOW_INFO_MODAL:
        return renderModal({
          title: codi_header,
          onClose: modalDismissRef?.current,
          Component: params.Component,
        })
      case CDActionTypes.SHOW_KEYWORD_MODAL:
        return renderModal({
          title: codi_header,
          onClose: modalDismissRef?.current,
          Component: params.Component,
        })
      case CDActionTypes.SHOW_INDEXES_MODAL:
        return renderModal({
          title: codi_header,
          Component: params.Component,
          onClose: modalDismissRef?.current,
          withProps: () => ({
            onItemClick: (info, { animate = NullAsDefault }) => {
              animate()
              onDismiss()
            },
          }),
        })
      case CDActionTypes.SHOW_ADMIN_MODAL:
        return renderModal({
          title: 'settings',
          Component: params.Component,
          onClose: modalDismissRef?.current,
        })
      case CDActionTypes.SHOW_LINKED_ARTICLES_MODAL:
      case CDActionTypes.SHOW_LINKED_DOCUMENTS_MODAL:
      case CDActionTypes.SHOW_LINKED_CODI_DOCUMENTS_MODAL:
      case CDActionTypes.SHOW_LINKED_PRECEDENT_DOCUMENTS_MODAL:
        return renderModal({
          title: codi_header,
          Component: params.Component,
          onClose: modalDismissRef?.current,
        })
      case CDActionTypes.UPDATE_CODI_ROW_TYPE:
        return (
          <Modal
            footer={null}
            onCancel={onDismiss}
            destroyOnHidden={true}
            className="custom-modal"
            open={modal_action.type}>
            <UpdateCodiRowType
              onCancel={onDismiss}
              onSuccess={onDismiss}
              codi_document_id={item?.id}
              params={modal_action.value}
            />
          </Modal>
        )
      case CDActionTypes.ARRANGE_CONTENT:
        return (
          <FullSideModalLayout
            center={true}
            renderHeader={NullAsDefault}
            onCancel={onDismiss}>
            <ArrangeCodiContent
              id={item?.id}
              onCancel={onDismiss}
              onSuccess={onDismiss}
              params={modal_action.value}
              renderActions={renderModalFooter}
            />
          </FullSideModalLayout>
        )
      case CDActionTypes.CANCEL_CONTENT:
        return (
          <FullSideModalLayout
            center={true}
            renderHeader={NullAsDefault}
            onCancel={onDismiss}
            contentStyle={{ minHeight: 0 }}>
            <CancelCodiContent
              id={item?.id}
              onCancel={onDismiss}
              onSuccess={onDismiss}
              params={modal_action.value}
              renderActions={renderModalFooter}
            />
          </FullSideModalLayout>
        )
      case CDActionTypes.EDIT_CONTENT:
        return (
          <FullSideModalLayout
            contentStyle={{ width: '660px' }}
            center={true}
            renderHeader={NullAsDefault}
            onCancel={onDismiss}>
            <EditCodiContent
              id={item?.id}
              onCancel={onDismiss}
              onSuccess={onDismiss}
              params={modal_action.value}
              renderActions={renderModalFooter}
            />
          </FullSideModalLayout>
        )
      case CDActionTypes.AMEND_CONTENT:
        return (
          <FullSideModalLayout
            contentStyle={{ width: '660px' }}
            center={true}
            renderHeader={NullAsDefault}
            onCancel={onDismiss}>
            <AmendCodiContent
              id={item?.id}
              onCancel={onDismiss}
              onSuccess={onDismiss}
              params={modal_action.value}
              renderActions={renderModalFooter}
            />
          </FullSideModalLayout>
        )
      case CDActionTypes.INSERT_CONTENT:
        return (
          <FullSideModalLayout
            contentStyle={{ width: '660px' }}
            center={true}
            renderHeader={NullAsDefault}
            onCancel={onDismiss}>
            <InsertCodiContent
              id={item?.id}
              onCancel={onDismiss}
              onSuccess={onDismiss}
              params={modal_action.value}
              renderActions={renderModalFooter}
            />
          </FullSideModalLayout>
        )
      case CDActionTypes.ADD_CONTENT:
        return (
          <FullSideModalLayout
            contentStyle={{ width: '660px' }}
            center={true}
            renderHeader={NullAsDefault}
            onCancel={onDismiss}>
            <AddCodiContent
              id={item?.id}
              onCancel={onDismiss}
              onSuccess={onDismiss}
              params={modal_action.value}
              renderActions={renderModalFooter}
            />
          </FullSideModalLayout>
        )
      default:
        break
    }
    return null
  }, [item, modal_action, modalDismissRef])

  const decrypted_item = useMemo(
    () => ({
      ...(item ?? {}),
      rows: item?.rows ? decryptContent(item?.rows, item) : [],
    }),
    [item]
  )

  return (
    <CodiDocumentContext.Provider
      key={id}
      value={{
        error,
        status,
        document,
        isLoading,
        isSucceed,
        activeKey,
        error_code,
        modalDismissRef,
        errorMessage,
        handleAction,
        getRowCacheId,
        handleMenuClick,
        item: decrypted_item,
        storage: {
          ...data_storage,
          isLoading: _.get(history_info, 'isLoading', false),
        },
        ...props,
        ...rest,
      }}>
      <PermissionProvider
        permissions={{
          edit: !!_.get(decrypted_item, 'edit'),
          ..._.get(decrypted_item, 'available_actions', {}),
        }}>
        <React.Fragment>
          {children}
          {modal_content}
          {isSucceed &&
            renderIf(
              useExtra,
              <Async
                key={id}
                {...{
                  query: bindQueryParam({ id }),
                  apiInfo: docs_getCodiExtraDataById_Api,
                }}
              />
            )}
        </React.Fragment>
      </PermissionProvider>
    </CodiDocumentContext.Provider>
  )
}

export const useRowDetails = (row_id) => {
  const { handleAction = NullAsDefault } = useContext(CodiDocumentContext)
  const {
    response,
    isLoading = false,
    ...rest
  } = useAsync({
    query: bindQueryParam({ id: row_id }),
    apiInfo: docs_getCodiDocumentsRowDetails_Api,
  })
  const item = getResponseItem(response)

  useEffect(() => {
    if (!_.isEmpty(item)) {
      handleAction(CDActionTypes.ROW_CLICKED, {
        ...item,
        useDetail: false,
      })
    }
  }, [response])

  return { item, isLoading, ...rest }
}

export const UseCodiRowById = ({ id, children, autoLoading = true }) => {
  if (_.isFunction(children)) {
    return (
      <Async
        key={id}
        {...{
          query: bindQueryParam({ id }),
          apiInfo: docs_getCodiDocumentsRowDetails_Api,
        }}>
        {({ response, isLoading = false, ...rest }) => {
          if (autoLoading && !!isLoading) {
            return <LoadingBlock />
          }
          const item = getResponseItem(response)
          return children({ item, isLoading, ...rest })
        }}
      </Async>
    )
  }
  return children
}

export default CodiDocumentContext
